import React from 'react';
import { Link } from 'react-router-dom';
import styles from './MarketingPage.module.css';
import sectionStyles from '../components/marketing/sections/sections.module.css';
import CTAGroup from '../components/marketing/CTAGroup';
import FinalCTASection from '../components/marketing/sections/FinalCTASection';
import StatusTag from '../components/marketing/StatusTag';
import useDocumentMeta from '../hooks/useDocumentMeta';
import useReveal from '../hooks/useReveal';
import { CheckIcon } from '../components/marketing/MarketingIcons';

const INCLUDED = [
  'A digital profile you can share by link today, and update at any time',
  'Contact capture, including from people without a Velox account',
  'Contacts that stay current as the other person updates their details',
  'The location and date of every contact you make, and a map of the year',
  'Private notes on each contact',
  'Profile view, link click and card tap analytics',
  'Paper business card scanning, with invitations to connect on Velox',
  'A weekly digest and daily brief on your industry',
  'Event suggestions matched to your industry and location',
  'An optional contactless card, linked to the same profile',
];

const COMING = [
  'Automated AI follow-ups drafted from your notes',
  'Which of your contacts are attending the events you are considering',
  'Designing and ordering a custom card without leaving the site',
];

const STEPS = [
  {
    title: 'Create your profile',
    body: 'Register, add your role, company, contact details and links. Whatever you change later is what people see, including those you met months ago. You do not need a physical card to start.',
  },
  {
    title: 'Start using Velox',
    body: 'Share a link to your profile, capture the people you meet, keep notes and find them again on a map. The platform is useful from the first conversation.',
  },
  {
    title: 'Add a card when you want one',
    body: 'A standard Velox card can be ordered from our shop at veloxco.com and arrives ready to activate. If you want your own design, tell us what you have in mind and our team will work the artwork up with you. Either card opens the same live profile.',
  },
];

const ProfessionalsPage = () => {
  const revealRef = useReveal();

  useDocumentMeta({
    title: 'Velox for professionals',
    description:
      'Velox for individual professionals: contacts whose details never go out of date, the context of where you met, market intelligence and your own analytics. Start with a profile; a card is optional. No company account required.',
    path: '/professionals',
  });

  return (
    <div ref={revealRef}>
      <section className={styles.pageHero}>
        <div className="vxm-grid-motif" />
        <div className={`vxm-container vxm-container--narrow ${styles.pageHeroInner}`}>
          <p className="vxm-eyebrow">For professionals</p>
          <h1 className="vxm-h1">Everyone you meet, still useful</h1>
          <p className="vxm-lead">
            You do not need your employer&rsquo;s permission, a company account,
            or a physical card to start. Create a profile and you have the
            platform: contacts that never go stale, a record of every
            introduction, and a way back to the person when they matter.
          </p>
          <CTAGroup source="professionals_page_hero" secondary="shop" />
        </div>
      </section>

      <section className="vxm-section vxm-section--raised">
        <div className="vxm-container vxm-container--narrow">
          <div className="vxm-reveal">
            <h2 className="vxm-h2">What is included</h2>
            <ul className={sectionStyles.featureList} style={{ marginTop: 'var(--vxm-space-5)' }}>
              {INCLUDED.map((item) => (
                <li key={item} className={sectionStyles.featureItem}>
                  <CheckIcon
                    className={sectionStyles.featureCheck}
                    width={18}
                    height={18}
                  />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="vxm-reveal" style={{ marginTop: 'var(--vxm-space-7)' }}>
            <h3 className={sectionStyles.featureGroupTitle}>
              In development
              <StatusTag>Committed</StatusTag>
            </h3>
            <ul
              className={`${sectionStyles.featureList} ${sectionStyles.featureListMuted}`}
            >
              {COMING.map((item) => (
                <li key={item} className={sectionStyles.featureItem}>
                  <CheckIcon
                    className={sectionStyles.featureCheck}
                    width={18}
                    height={18}
                  />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="vxm-section">
        <div className="vxm-container vxm-container--narrow">
          <div className="vxm-reveal">
            <h2 className="vxm-h2">Getting started</h2>
          </div>
          <ol className={styles.detailList} style={{ marginTop: 'var(--vxm-space-6)' }}>
            {STEPS.map((step, index) => (
              <li key={step.title} className={`${styles.detailRow} vxm-reveal`}>
                <h3 className={styles.detailTerm}>
                  {index + 1}. {step.title}
                </h3>
                <p className={styles.detailBody}>{step.body}</p>
              </li>
            ))}
          </ol>

          <div className={`${styles.noteBox} vxm-reveal`}>
            <p>
              <strong>Already have a card from your employer?</strong> Use the
              invitation they sent you rather than buying another. Your
              seat is managed by their Velox account.
            </p>
          </div>
        </div>
      </section>

      <section className="vxm-section vxm-section--raised">
        <div className="vxm-container vxm-container--narrow">
          <div className={`${styles.panel} vxm-reveal`}>
            <h2 className={styles.panelTitle}>Thinking about your whole team?</h2>
            <p className="vxm-body" style={{ marginBottom: 'var(--vxm-space-5)' }}>
              If several colleagues are already using Velox, a company account
              adds the shared contact hub, seat management and a comparison of
              how everyone is performing.
            </p>
            <Link to="/enterprise" className="vxm-btn vxm-btn--secondary">
              See Velox for teams
            </Link>
          </div>
        </div>
      </section>

      <FinalCTASection
        title="Create your profile"
        body="Start with Velox today. Add a standard card from the shop, or tell us if you want one designed with you."
        source="professionals_final"
        secondary="shop"
      />
    </div>
  );
};

export default ProfessionalsPage;
